import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from "@angular/common";
import {IDialogComponent} from "../../../../../common/dialog/iDialog.component";
import {DialogWithInput} from "../../../../../common/dialog/dialogWithInput";
import {
  MaterializePlateFrame
} from "../../agent/materializeAgentPlate";
import {DialogModule} from "../../../../../common/dialog/dialog.module";
import {CardModule} from "../../../../../core/component/card/card.module";
import {BaseView} from "../../../../../core/view/base.view";
import {MaterializePlateExamControl, PressureGravityCenter} from "../../control/materializePlateExam.control";
import {MaterialModule} from "../../../../../../material.module";
import {FormsModule} from "@angular/forms";
import {ButtonModule} from "../../../../../core/component/button/button.module";
import {interval, Subscription} from "rxjs";
import {
  DrawingOnImageChangedEvent,
  DrawingOnImageComponent, DrawingOption,
  DrawingOptions
} from "../../../../../common/drawing/drawing-on-image/drawing-on-image.component";
import {DrawingData} from "../../../../../common/drawing/drawing-on-image/drawingOnImageDrawingElement.model";
import {InputDrawingData} from "../../../../../common/drawing/drawing-on-image/drawingOnImageInputData";

export interface MaterializePlateExamDynamicFootComparisonDialogInput {
  feet: MaterializePlateExamDynamicFootComparisonDialogInputFoot[];
  sensorWidth: number;
  sensorHeight: number;
  frameRate: number;
}

export class MaterializePlateExamDynamicFootComparisonDialogInputFoot {
  durationInMillis: number;
  picture: string;
  type: 'left' | 'right';
  data: FootRawData;
  drawableData: InputDrawingData;
}


export interface MaterializePlateExamDynamicFootComparisonDialogOutput{
  left: FootData;
  right: FootData;
}

@Component({
  selector: 'app-materialize-plate-exam-dynamic-foot-comparison-dialog',
  templateUrl: './materialize-plate-exam-dynamic-foot-comparison-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    CardModule,
    MaterialModule,
    FormsModule,
    ButtonModule,
    DrawingOnImageComponent,
  ],
})
export class MaterializePlateExamDynamicFootComparisonDialogComponent extends BaseView
    implements IDialogComponent<MaterializePlateExamDynamicFootComparisonDialogInput, MaterializePlateExamDynamicFootComparisonDialogOutput>, OnInit, AfterViewInit, OnDestroy {
  dialog: DialogWithInput<MaterializePlateExamDynamicFootComparisonDialogInput, MaterializePlateExamDynamicFootComparisonDialogOutput>;

  @ViewChild('canvas1', { static: true }) canvas1!: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvas2', { static: true }) canvas2!: ElementRef<HTMLCanvasElement>;
  speed = 2;
  feet: MaterializePlateExamDynamicFootComparisonDialogInputFoot[];
  intervalSubscription: Subscription;
  sliderMaxValue = 0;
  sliderValue = 0;
  values: FootData[] = [];
  duration: number;
  rotation = 0;
  currentTab: 'images' | 'timelapse' = 'timelapse';
  drawableImageOptions: DrawingOptions[];

  constructor(private materializePlateExamControl: MaterializePlateExamControl) {
    super();
    this.drawableImageOptions = [{
      options: [
        this.buildDrawableOption('L')
      ]
    },
      {
        options: [
          this.buildDrawableOption('R')
        ]
      }
    ]
  }

  ngOnInit() {
    if (this.dialog.input.feet?.length !== 2) {
      this.dialog.close();
      throw new Error('Please provide exactly 2 feet');
    }
    this.feet = this.dialog.input.feet.sort((a, b) => {
      if (a.type === 'left' && b.type === 'right') return -1;
      if (a.type === 'right' && b.type === 'left') return 1;
      return 0;
    });
  }

  ngAfterViewInit() {
    const leftFoot = this.dialog.input.feet[0];
    const rightFoot = this.dialog.input.feet[1];
    if (leftFoot.data?.frames?.length > this.sliderMaxValue - 1) {
      this.sliderMaxValue = leftFoot.data.frames?.length - 1;
    }
    if (rightFoot.data?.frames?.length > this.sliderMaxValue - 1) {
      this.sliderMaxValue = rightFoot.data?.frames?.length - 1;
    }

    this.values = [
        this.toValue(leftFoot),
        this.toValue(rightFoot),
    ];
    this.refreshImage(0);
    this.refreshImage(1);


    /* Promise.all([
      this.fetchFootPressureRawData(this.feet[0]),
      this.fetchFootPressureRawData(this.feet[1]),
    ]).then((values: MaterializePlateFootRawData[]) => {
      if (values[0].frames?.length > this.sliderMaxValue - 1) {
        this.sliderMaxValue = values[0].frames?.length - 1;
      }
      this.plate = values[0].common.plate;

      if (values[1].frames?.length > this.sliderMaxValue - 1) {
        this.sliderMaxValue = values[1].frames?.length - 1;
      }
      this.values = values.map((value, index) => {
        const mirrorXFrames = value.frames.map(value1 => {
          return {
            data: this.mirrorXFrame(value1.data)
          };
        });

        return {
          picture: this.feet[index].picture,
          pressureData: {
            common: value.common,
            frames: mirrorXFrames,
          },
          gravityCenters: mirrorXFrames.map(frame => this.materializePlateExamControl.computeCenterOfGravity(frame.data)),
          durationInMillis: value.common.durationInMillis
        };
      });

    }).catch((e) => this.toastControl.presentError(e, 'Visualisation des mesures impossibles'))
  */
  }

  ngOnDestroy() {
    this.pause();
  }

  buildDrawableOption(side: 'L' | 'R'): DrawingOption{
    return {
      name: 'ouverture-angle-pas-' + side,
      label: 'Angle de pas',
      size: 15,
      color: 'black',
      thickness: 1,
      element: 'angle',
      subType: 'VERTICAL'
    };
  }

  toValue(inputFoot: MaterializePlateExamDynamicFootComparisonDialogInputFoot): FootData {
    return {
      originalPicture: inputFoot.picture,
      pictureUrl: undefined,
      pressureData: inputFoot.data,
      gravityCenters: inputFoot.data.frames.map(frame => this.materializePlateExamControl.computeCenterOfGravity(frame.data)),
      durationInMillis: inputFoot.durationInMillis,
      drawableData: undefined
    };
  }

  private rotateRows(rawData: FootRawData, rotation: number){
    for (let i = 0; i < rawData.frames.length; i++) {
      rawData.frames[i].data = this.materializePlateExamControl.rotateData(rawData.frames[i].data, rotation);
    }
  }

  /* fetchFootPressureRawData(foot: MaterializePlateExamDynamicComponentFoot): Promise<MaterializePlateFootRawData> {
    return this.dialog.input.materializeAgent.fetchFootPressureRawData(foot.recordingName, foot.index)
  }*/

  refreshCanvas(frameIndex: number, index: number) {
    this.coreRefreshCanvas(index === 0 ? this.canvas1 : this.canvas2, this.values[index], frameIndex);
    this.duration = (frameIndex + 1) / this.frameRate * 1000;
  }

  coreRefreshCanvas(canvas: ElementRef<HTMLCanvasElement>,
                    footData: FootData,
                    index: number) {

    const frame = index < footData.pressureData.frames.length -1 ?
        footData.pressureData.frames[index] :
        footData.pressureData.frames[footData.pressureData.frames.length -1]
    this.refreshFrame(canvas, frame,
        footData.gravityCenters.slice(0, index + 1));
  }

  drawingOnImageChanged(event: DrawingOnImageChangedEvent, index: number) {
    this.values[index].drawableData = event;
  }

  refreshFrame(canvas: ElementRef<HTMLCanvasElement>, frame: MaterializePlateFrame,
               gravityCenters: PressureGravityCenter[]) {

    // WARNING. For unknown reason we have to mirror X (frame + gravity centers)
    this.materializePlateExamControl.drawPressureData(canvas, frame.data, this.sensorWidth, this.sensorHeight, true, true, 2);

    const gravityCenter = gravityCenters.length > 0
        ? gravityCenters[gravityCenters.length - 1]
        : null;
    if (gravityCenter) {
      this.materializePlateExamControl.drawGravityCenterAsCross(canvas, gravityCenter,
          this.sensorWidth, this.sensorHeight, 2);
    }

    this.materializePlateExamControl.drawCenterOfGravities(canvas?.nativeElement, gravityCenters, frame.data, this.sensorWidth, this.sensorHeight, 2, 2);
  }

  mirrorXFrame(data: number[][]): number[][] {
    if (!data || data.length === 0) {
      return [];
    }
    return data.map(row => [...row].reverse());
  }

  rotationChanged(indexes: number[]) {
    this.rotation += 90;
    indexes.forEach((index) => {
      this.rotateRows(this.values[index].pressureData, 90);
      this.values[index].gravityCenters = this.values[index].pressureData.frames.map(frame => this.materializePlateExamControl.computeCenterOfGravity(frame.data))
      this.refreshCanvas(this.sliderValue, index);
      this.refreshImage(index);
    });
  }

  refreshImage(index: number) {
    const frames = this.values[index].pressureData.frames;
    const pictureUrl = this.values[index].pictureUrl;
    const pictureBase64 = this.values[index].originalPicture;
    const gravityCenters = this.values[index].gravityCenters;

    if (pictureUrl) {
      URL.revokeObjectURL(pictureUrl);
    }

    const image = new Image();
    image.src = pictureBase64;

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Dessiner l'image
      ctx.drawImage(image, 0, 0);
      this.materializePlateExamControl.drawCenterOfGravities(canvas, gravityCenters, frames[0].data, this.sensorWidth, this.sensorHeight, 2, 2);
      this.values[index].pictureUrl = canvas.toDataURL();
    };
  }

  start() {
    this.intervalSubscription = interval(100).subscribe({
          next: () => {
              if (this.sliderValue + this.speed <= this.sliderMaxValue) {
                this.sliderValue += this.speed;
                this.refreshCanvas(this.sliderValue, 0);
                this.refreshCanvas(this.sliderValue, 1);
              } else {
                this.sliderValue = 0;
              }
          },
          complete:() => {
            this.intervalSubscription = undefined
          }
    });
  }
  pause() {
    if (this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
      this.intervalSubscription = undefined;
    }
  }

  close() {
    this.dialog.close({
      left: this.values[0],
      right: this.values[1],
    });
  }

  get sensorWidth(): number {
    return this.dialog.input.sensorWidth
  }

  get sensorHeight(): number {
    return this.dialog.input.sensorHeight;
  }

  get frameRate(): number {
    return this.dialog.input.frameRate;
  }
}



export class FootData {
  pressureData: FootRawData;
  gravityCenters: PressureGravityCenter[] = [];
  durationInMillis: number;
  pictureUrl: string;
  originalPicture: string;
  drawableData: DrawingOnImageChangedEvent;
}


export class FootRawData {
  cellWidth: number;
  cellHeight: number;
  frames: MaterializePlateFrame[] = [];
}




